import React from 'react';
import { useAuth } from '@/ui/auth/AuthProvider';
import { Loader2, ShieldCheck } from 'lucide-react';
import hivemindLogo from '/hivemind-logo.svg';

export function LoginScreen() {
    const { signInWithGoogle, loading } = useAuth();
    const [isSigningIn, setIsSigningIn] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleSignIn = async () => {
        try {
            setIsSigningIn(true);
            setError(null);
            await signInWithGoogle();
        } catch (err) {
            console.error('Login failed:', err);
            setError('Failed to sign in. Please try again.');
        } finally {
            setIsSigningIn(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-md space-y-8 text-center">
                {/* Logo */}
                <div className="flex flex-col items-center gap-4">
                    <div className="w-20 h-20 bg-background-secondary rounded-2xl flex items-center justify-center border border-border shadow-xl">
                        <img src={hivemindLogo} alt="Hivemind" className="w-12 h-12" />
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight">Hivemind</h1>
                    <p className="text-foreground-secondary">
                        Secure, AI-powered development environment
                    </p>
                </div>

                {/* Login Box */}
                <div className="bg-background-secondary border border-border rounded-xl p-8 shadow-sm space-y-6">
                    <div className="space-y-2">
                        <h2 className="text-xl font-semibold">Welcome Back</h2>
                        <p className="text-sm text-foreground-muted">
                            Sign in to access your projects and peers
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-sm p-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleSignIn}
                        disabled={isSigningIn}
                        className="w-full btn-primary py-3 flex items-center justify-center gap-3 text-base"
                    >
                        {isSigningIn ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                        )}
                        Sign in with Google
                    </button>

                    <div className="flex items-center justify-center gap-2 text-xs text-foreground-muted">
                        <ShieldCheck className="w-3 h-3" />
                        <span>Secure authentication via Firebase</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
